import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	AreaChart,
	Area,
	Scatter,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";
import divider from "../../assets/divider-logo.3699f955.svg";

interface DateRange {
  baseline: number | null;
  current: number | null;
}
interface Props {
	chartData: any;
	heading: any;
	goLiveDate: any;
	page: any;
	dateRange?:DateRange
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength } = props;
	const d = 40; // distance from point to label
	const angle = 120; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);
	const shouldShow = index === 0 || index === dataLength - 1;
	return (
		<g>
			{shouldShow && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x - xOffset}
						y2={y - yOffset}
						stroke="gray"
						opacity={0.5}
					/>
					<text
						x={x - xOffset + 20}
						y={y - yOffset - 5}
						fill="gray"
						fontSize={`${page == "summary" ? "1.4vh" : "1.2vh"}`}
						opacity={`${page == "summary" ? 0.8 : 1}`}
						textAnchor="end"
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength } = props;
	const d = 60; // distance from point to label
	const angle = 120; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);
	const shouldShow = index === 0 || index === dataLength - 1;
	return (
		<g>
			{shouldShow && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x + xOffset}
						y2={y - yOffset}
						stroke="#012169"
						opacity={0.5}
					/>
					<text
						x={x + xOffset - 5}
						y={y - yOffset - 5}
						fill="#012169"
						fontSize={`${page == "summary" ? "1.4vh" : "1.2vh"}`}
						opacity={`${page == "summary" ? 0.8 : 1}`}
						textAnchor="start"
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};

const NetCostReductionChart = (props: Props) => {
	let data: any = props?.chartData?.finalData;
	let avgReductionValue: any = props?.chartData?.averageReduction;
	const { page } = props;
	const { goLiveDate } = props;
	let minValue = 0;
	let maxValue = 0;
	let customTicks: any = [];
	if (data?.length > 0) {
		const minPrev = data.reduce((min: any, obj: any) => {
			return obj.total_avg_cost_Prev < min
				? obj.total_avg_cost_Prev
				: min;
		}, Infinity);
		const minCurr = data.reduce((min: any, obj: any) => {
			return obj.total_cost_Curr < min ? obj.total_cost_Curr : min;
		}, Infinity);
		const maxPrev = data.reduce((max: any, obj: any) => {
			return obj.total_avg_cost_Prev > max
				? obj.total_avg_cost_Prev
				: max;
		}, -Infinity);
		const maxCurr = data.reduce((max: any, obj: any) => {
			return obj.total_cost_Curr > max ? obj.total_cost_Curr : max;
		}, -Infinity);
		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;
		data?.map((item: any, k: any) => {
			goLiveDate.map((g: any, i: any) => {
				const finalFormattedDate = moment
					.utc(g.golive_date)
					.format("YYYY-MM-DD");
				if (
					moment.utc(item.week).format("YYYY-MM-DD") ==
					finalFormattedDate
				) {
					item.master_vendor_name =
						g.master_vendor_name == "BRIDGEPORT FITTINGS, INC."
							? "BP"
							: g.master_vendor_name == "THOMAS & BETTS"
							? "T&B"
							: g.master_vendor_name;
					item.dotValue = item.total_cost_Curr;
				}
			});
			item.range = [item.total_cost_Curr, item.total_avg_cost_Prev];
			item.formattedWeek = item.monthly
				? moment.utc(item.week).format("MMM")
				: moment.utc(item.week).format("MMM DD");
			if (item.monthly) {
				customTicks.push(item.formattedWeek);
			} else {
				if (k % 4 === 0) {
					customTicks.push(item.formattedWeek);
				}
			}
		});
	}
	let heading = props?.heading;
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">
							{data.monthly ? "Month" : "Week"}:
						</div>
						<div
							className="font-bold ml-1"
							style={{ color: "#3f3f3f" }}
						>
							{data.formattedWeek}
						</div>
					</div>

					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Cost {props?.dateRange?.current||2025} :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.total_cost_Curr, 2, "$")}
							</label>
						</p>
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Cost {props?.dateRange?.baseline||2024} :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(
									data.total_avg_cost_Prev,
									2,
									"$"
								)}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`graphBox !w-full  h-[100%]`}>
			{/* <div
				className={`bg-[#ffff] graphBorder flex ${
					page == "summary" ? "" : "flex-col"
				} justify-between`}
			>
				<p className={`text-center graphTitle   pl-[1vh]`}>
					<span>1. </span>
					{heading}
				</p>
				<p className="flex justify-end px-[2vh] font-semibold text-[1.8vh] mt-[0] text-[#40a108]">
					{numberFormatter(avgReductionValue?.percentage * 100, 1)}%,{" "}
					{numberFormatter(avgReductionValue?.cost, 1, "$")} in Net
					Cost Reductions
				</p>
			</div> */}
			<div className="headSpace">
				<span className="headContent !text-[1vw]"> {heading}</span>
				<p className="flex justify-end px-[2vh] font-semibold textNormal mt-[0] text-[#40a108]">
					{numberFormatter(avgReductionValue?.percentage * 100, 1)}%,{" "}
					{numberFormatter(avgReductionValue?.cost, 1, "$")} in Net
					Cost Reductions
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height="65%"
				className="bg-[#ffff] "
			>
				<ComposedChart
					data={data}
					margin={{
						top: 60,
						right: 40,
						left: 40,
					}}
				>
					<XAxis
						dataKey="formattedWeek"
						className="graph-label-small "
						stroke="black"
						interval={1}
						ticks={customTicks}
					/>
					<YAxis
						hide
						domain={[
							(dataMin: any) => dataMin / 1.5,
							(dataMax: any) => dataMax * 0.9,
						]}
					/>

					<Area dataKey="range" stroke="#dce0a7" fill="#dce0a7" />
					<Line
						type="linear"
						dataKey="total_avg_cost_Prev"
						stroke="gray"
						name={`2024`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={2}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={data}
											color={"gray"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="total_avg_cost_Prev"
							content={($) => (
								<CustomLabel
									payload={$}
									page={page}
									dataLength={data.length}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="total_cost_Curr"
						name={`2025`}
						stroke="#012169"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={2}
						// dot={page == 'summary' ? true : false}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={data}
											color={"#012169"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="total_cost_Curr"
							content={($) => (
								<CustomLabel2
									payload={$}
									page={page}
									dataLength={data.length}
								/>
							)}
						/>
					</Line>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>
					{page == "summary" && (
						<Scatter
							dataKey="dotValue"
							fill="none"
							legendType="none"
						>
							<LabelList
								dataKey="master_vendor_name"
								fill="black"
								fontWeight={700}
								fontSize="1.2vh"
								formatter={(value: number) =>
									`${value} Go-Live`
								}
								position="bottom"
							/>
						</Scatter>
					)}
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter !justify-end">
				<CustomLegend dateRange={props?.dateRange}></CustomLegend>
			</div>
		</div>
	);
};
const CustomLegend = (props:any) => (
	<div
		style={{
			display: "flex",
			gap: "2rem",
			alignItems: "center",
			color: "black",
			fontSize: "0.8rem",
		}}
	>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "gray",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.baseline||2024}
		</span>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "#012169",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.current||2025}
		</span>
	</div>
);
const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default NetCostReductionChart;
